import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, Search, Bell, CalendarDays, Hash, RadioTower, MapPin, Copy, CheckCircle2, X } from 'lucide-react';
import { styles } from '../styles';
import {
    getDailyRangesConfig,
    getDailyRangesSummary,
    getDailyRangesList,
    getDailyRangeValues,
    getDailyRangeDetails,
    updateDailyRangesConfig
} from '../services/api/dailyRangesApi';

const PAGE_SIZE = 500;

const DailyRanges = () => {
    const [loading, setLoading] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);
    const [error, setError] = useState('');
    const [days, setDays] = useState(3);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [constantOnly, setConstantOnly] = useState(false);
    const [config, setConfig] = useState({ telegram_enabled: false });
    const [summary, setSummary] = useState({ total_ranges: 0, constant_ranges: 0, new_ranges_today: 0, total_occurrences: 0, by_day: [] });
    const [rows, setRows] = useState([]);
    const [listMeta, setListMeta] = useState({ total: 0, limit: PAGE_SIZE, offset: 0, has_more: false, anchor_date: null });
    const [currentPage, setCurrentPage] = useState(1);
    const [copiedKey, setCopiedKey] = useState('');
    const [copyingAllRanges, setCopyingAllRanges] = useState(false);
    const [selectedRange, setSelectedRange] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState('');
    const [rangeDetails, setRangeDetails] = useState(null);

    const copyText = useCallback(async (text, successKey) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(successKey);
            setTimeout(() => {
                setCopiedKey((prev) => (prev === successKey ? '' : prev));
            }, 1800);
        } catch (_error) {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopiedKey(successKey);
            setTimeout(() => {
                setCopiedKey((prev) => (prev === successKey ? '' : prev));
            }, 1800);
        }
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(timeoutId);
    }, [query]);

    useEffect(() => {
        const controller = new AbortController();
        getDailyRangesConfig({ signal: controller.signal })
            .then((value) => setConfig(value || { telegram_enabled: false }))
            .catch((err) => {
                if (err.name !== 'AbortError') setError(err.message);
            });
        return () => controller.abort();
    }, []);

    const load = useCallback(async (page = currentPage) => {
        setLoading(true);
        setError('');
        try {
            const offset = (Math.max(1, page) - 1) * PAGE_SIZE;
            const [sumResult, listResult] = await Promise.allSettled([
                getDailyRangesSummary(days),
                getDailyRangesList({ days, constant: constantOnly, q: debouncedQuery, limit: PAGE_SIZE, offset })
            ]);

            const failures = [];

            if (sumResult.status === 'fulfilled') {
                setSummary(sumResult.value || {});
            } else {
                setSummary({ total_ranges: 0, constant_ranges: 0, new_ranges_today: 0, total_occurrences: 0, by_day: [] });
                failures.push(`summary: ${sumResult.reason?.message || 'failed'}`);
            }

            if (listResult.status === 'fulfilled') {
                setRows(listResult.value?.rows || []);
                setListMeta(listResult.value?.meta || { total: 0, limit: PAGE_SIZE, offset, has_more: false });
            } else {
                setRows([]);
                failures.push(`list: ${listResult.reason?.message || 'failed'}`);
            }

            if (failures.length) {
                setError(`Some Daily Ranges data failed to load (${failures.join(', ')})`);
            }
        } catch (err) {
            if (err.name !== 'AbortError') setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentPage, days, constantOnly, debouncedQuery]);

    useEffect(() => {
        const controller = new AbortController();
        const run = async () => {
            setLoading(true);
            setError('');
            try {
                const offset = (Math.max(1, currentPage) - 1) * PAGE_SIZE;
                const [sumResult, listResult] = await Promise.allSettled([
                    getDailyRangesSummary(days, { signal: controller.signal }),
                    getDailyRangesList({ days, constant: constantOnly, q: debouncedQuery, limit: PAGE_SIZE, offset }, { signal: controller.signal })
                ]);

                const failures = [];
                if (sumResult.status === 'fulfilled') setSummary(sumResult.value || {});
                else failures.push(`summary: ${sumResult.reason?.message || 'failed'}`);
                if (listResult.status === 'fulfilled') {
                    setRows(listResult.value?.rows || []);
                    setListMeta(listResult.value?.meta || { total: 0, limit: PAGE_SIZE, offset, has_more: false });
                } else {
                    setRows([]);
                    failures.push(`list: ${listResult.reason?.message || 'failed'}`);
                }
                if (failures.length) setError(`Some Daily Ranges data failed to load (${failures.join(', ')})`);
            } catch (err) {
                if (err.name !== 'AbortError') setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        run();
        return () => controller.abort();
    }, [days, constantOnly, debouncedQuery, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [days, constantOnly, debouncedQuery]);

    const onToggleTelegram = async () => {
        setSavingConfig(true);
        try {
            const next = !config.telegram_enabled;
            const updated = await updateDailyRangesConfig(next);
            setConfig(updated);
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingConfig(false);
        }
    };

    const openRangeDetails = async (rangeValue) => {
        setSelectedRange(rangeValue);
        setDetailsLoading(true);
        setDetailsError('');
        setRangeDetails(null);
        try {
            const details = await getDailyRangeDetails({ range: rangeValue, days });
            setRangeDetails(details);
        } catch (err) {
            setDetailsError(err.message || 'Failed to load range details');
        } finally {
            setDetailsLoading(false);
        }
    };

    const closeRangeDetails = () => {
        setSelectedRange(null);
        setRangeDetails(null);
        setDetailsError('');
        setDetailsLoading(false);
    };

    useEffect(() => {
        if (!selectedRange || typeof document === 'undefined') {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setSelectedRange(null);
                setRangeDetails(null);
                setDetailsError('');
                setDetailsLoading(false);
            }
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [selectedRange]);

    const copyCurrentPageRanges = () => {
        const text = paginatedRows.map((row) => row.range_value).filter(Boolean).join('\n');
        copyText(text, 'page-ranges');
    };

    const copyAllFilteredRanges = async () => {
        setCopyingAllRanges(true);
        setError('');
        try {
            const result = await getDailyRangeValues({ days, constant: constantOnly, q: debouncedQuery });
            const text = (result.ranges || []).filter(Boolean).join('\n');
            await copyText(text, 'all-ranges');
        } catch (err) {
            setError(err.message || 'Failed to copy all filtered ranges');
        } finally {
            setCopyingAllRanges(false);
        }
    };

    const totalRows = Number(listMeta.total || rows.length || 0);
    const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const pageStart = Number(listMeta.offset || 0);
    const paginatedRows = rows;
    const pageStartCount = totalRows ? pageStart + 1 : 0;
    const pageEndCount = Math.min(pageStart + rows.length, totalRows);

    useEffect(() => {
        if (currentPage !== currentPageSafe) {
            setCurrentPage(currentPageSafe);
        }
    }, [currentPage, currentPageSafe]);

    const paginationPages = useMemo(() => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const pages = new Set([1, totalPages, currentPageSafe - 1, currentPageSafe, currentPageSafe + 1]);
        return Array.from(pages)
            .filter((page) => page >= 1 && page <= totalPages)
            .sort((a, b) => a - b);
    }, [currentPageSafe, totalPages]);

    const renderPagination = (location) => {
        if (!totalRows) {
            return null;
        }

        return (
            <div className={`daily-ranges-pagination daily-ranges-pagination-${location}`}>
                <div className="daily-ranges-pagination-summary">
                    Showing {pageStartCount}-{pageEndCount} of {totalRows} filtered ranges
                </div>
                <div className="daily-ranges-pagination-controls">
                    <button
                        type="button"
                        className="daily-ranges-page-button"
                        disabled={currentPageSafe === 1}
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    >
                        Prev
                    </button>
                    {paginationPages.map((page, index) => {
                        const previousPage = paginationPages[index - 1];
                        const showGap = previousPage && page - previousPage > 1;
                        return (
                            <React.Fragment key={page}>
                                {showGap ? <span className="daily-ranges-page-gap">...</span> : null}
                                <button
                                    type="button"
                                    className={`daily-ranges-page-button ${page === currentPageSafe ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </button>
                            </React.Fragment>
                        );
                    })}
                    <button
                        type="button"
                        className="daily-ranges-page-button"
                        disabled={currentPageSafe === totalPages}
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    };

    const cards = useMemo(() => ([
        { label: `Constant Ranges (${days} Day${days === 1 ? '' : 's'})`, value: summary.constant_ranges || 0, color: '#22c55e' },
        { label: 'New Ranges Today', value: summary.new_ranges_today || 0, color: '#f59e0b' },
        { label: 'Total Ranges', value: summary.total_ranges || 0, color: '#38bdf8' },
        { label: 'Total Occurrences', value: summary.total_occurrences || 0, color: '#ff006e' }
    ]), [days, summary]);

    return (
        <div style={styles.tableContainer}>
            <div style={styles.tableHeader}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <h2 style={styles.tableTitle}>Daily Ranges</h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={onToggleTelegram}
                            disabled={savingConfig}
                            style={{
                                ...styles.button,
                                backgroundColor: config.telegram_enabled ? '#22c55e' : '#64748b',
                                borderColor: config.telegram_enabled ? '#22c55e' : '#64748b',
                                color: '#fff'
                            }}
                        >
                            <Bell size={16} />
                            {config.telegram_enabled ? 'Telegram Summary On' : 'Telegram Summary Off'}
                        </button>
                        <button onClick={load} disabled={loading} style={{ ...styles.button, backgroundColor: '#3a86ff', borderColor: '#3a86ff', color: '#fff' }}>
                            <RefreshCw size={16} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
                            Refresh
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', minWidth: '260px' }}>
                        <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search range..."
                            style={{ ...styles.searchInput, paddingLeft: '32px' }}
                        />
                    </div>
                    <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={styles.select}>
                        <option value={1}>Today</option>
                        <option value={2}>Last 2 Days</option>
                        <option value={3}>Last 3 Days</option>
                    </select>
                    <select value={String(constantOnly)} onChange={(e) => setConstantOnly(e.target.value === 'true')} style={styles.select}>
                        <option value="false">All Ranges</option>
                        <option value="true">Constant Only</option>
                    </select>
                </div>
            </div>

            {error && <div style={styles.errorAlert}>{error}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '10px', marginBottom: '12px' }}>
                {cards.map((card) => (
                    <div key={card.label} style={{ background: 'rgba(15, 23, 42, 0.65)', border: `1px solid ${card.color}55`, borderRadius: '10px', padding: '10px' }}>
                        <div style={{ color: '#cbd5e1', fontSize: '12px' }}>{card.label}</div>
                        <div style={{ color: card.color, fontSize: '20px', fontWeight: 700 }}>{card.value}</div>
                    </div>
                ))}
            </div>

            {summary.anchor_date && (
                <div style={{ color: summary.stale ? '#f59e0b' : '#94a3b8', fontSize: '12px', marginBottom: '10px' }}>
                    Window ending {summary.anchor_date}{summary.stale ? ' (latest snapshot is older than today)' : ''}
                </div>
            )}

            {Array.isArray(summary.top_destinations) && summary.top_destinations.length > 0 && (
                <div style={{ border: '1px solid rgba(245, 158, 11, 0.28)', borderRadius: '10px', background: 'rgba(15, 23, 42, 0.62)', marginBottom: '12px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '10px 12px', borderBottom: '1px solid rgba(245, 158, 11, 0.18)' }}>
                        <div style={{ color: '#fde68a', fontSize: '13px', fontWeight: 800 }}>Top Destinations By Working Ranges</div>
                        <div style={{ color: '#94a3b8', fontSize: '11px' }}>{days} day window</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px', padding: '10px' }}>
                        {summary.top_destinations.map((destination, index) => (
                            <div key={destination.destination || index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', background: 'rgba(2, 6, 23, 0.5)', border: '1px solid rgba(148, 163, 184, 0.12)' }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ color: '#e2e8f0', fontSize: '12px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{destination.destination}</div>
                                    <div style={{ color: '#94a3b8', fontSize: '10px' }}>{destination.total_occurrences} occurrences {destination.active_all_days ? '• active all days' : ''}</div>
                                </div>
                                <div style={{ color: '#f59e0b', fontSize: '16px', fontWeight: 900 }}>{destination.working_ranges}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="daily-ranges-list-wrap">
                <div className="daily-ranges-list-header">
                    <div className="daily-ranges-list-title">Range Table</div>
                    <div className="daily-ranges-list-actions">
                        <button
                            type="button"
                            onClick={copyAllFilteredRanges}
                            disabled={!totalRows || copyingAllRanges}
                            className="daily-ranges-copy-all"
                        >
                            {copiedKey === 'all-ranges' ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                            {copiedKey === 'all-ranges'
                                ? `Copied All Ranges (${totalRows})`
                                : copyingAllRanges
                                    ? 'Copying All Ranges...'
                                    : `Copy All Ranges (${totalRows})`}
                        </button>
                        <button
                            type="button"
                            onClick={copyCurrentPageRanges}
                            disabled={!paginatedRows.length}
                            className="daily-ranges-copy-all"
                        >
                            {copiedKey === 'page-ranges' ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                            {copiedKey === 'page-ranges' ? 'Copied This Page' : `Copy This Page (${paginatedRows.length})`}
                        </button>
                        {renderPagination('top')}
                    </div>
                </div>

                <div className="daily-ranges-list">
                {paginatedRows.map((row) => (
                    <div key={row.range_value} className="daily-range-card">
                        <div className="daily-range-card-main" role="button" tabIndex={0} onClick={(e) => openRangeDetails(row.range_value, e)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openRangeDetails(row.range_value, e); }}>
                            <div className="daily-range-title-wrap">
                                <button
                                    type="button"
                                    className="daily-range-title-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openRangeDetails(row.range_value, e);
                                    }}
                                >
                                    {row.range_value}
                                </button>
                                <button
                                    type="button"
                                    className="daily-range-open-mini"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openRangeDetails(row.range_value, e);
                                    }}
                                >
                                    View SIDs
                                </button>
                                <button
                                    type="button"
                                    className="daily-range-copy-mini"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyText(row.range_value || '', `range-${row.range_value}`);
                                    }}
                                    title="Copy this range"
                                >
                                    {copiedKey === `range-${row.range_value}` ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                                    {copiedKey === `range-${row.range_value}` ? 'Copied' : 'Copy'}
                                </button>
                                <span className={`daily-range-pill ${row.is_constant ? 'ok' : 'warn'}`}>
                                    {row.is_constant ? 'Constant' : 'Not constant'}
                                </span>
                            </div>
                            <div className="daily-range-meta">
                                <span><CalendarDays size={14} /> {row.days_seen} day(s)</span>
                                <span><Hash size={14} /> {row.total_occurrences} occurrences</span>
                                <span><RadioTower size={14} /> {row.sid_count} SIDs</span>
                                <span><MapPin size={14} /> {row.destination_count} destinations</span>
                            </div>
                        </div>
                    </div>
                ))}
                    {!rows.length && !loading && (
                        <div style={{ padding: '14px', color: '#94a3b8' }}>No ranges found for current filters.</div>
                    )}
                </div>
                {renderPagination('bottom')}
            </div>

            <style>{`
                .daily-ranges-list-wrap {
                    border: 1px solid #1e293b;
                    border-radius: 10px;
                    overflow: hidden;
                }
                .daily-ranges-list-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    flex-wrap: wrap;
                    background: rgba(15, 23, 42, 0.8);
                    border-bottom: 1px solid #1e293b;
                    padding: 10px 12px;
                }
                .daily-ranges-list-actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .daily-ranges-list-title {
                    color: #cbd5e1;
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 0.4px;
                    text-transform: uppercase;
                }
                .daily-ranges-copy-all {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    min-height: 28px;
                    border-radius: 8px;
                    border: 1px solid #f59e0b;
                    background: rgba(245, 158, 11, 0.16);
                    color: #fde68a;
                    cursor: pointer;
                    padding: 4px 10px;
                    font-size: 12px;
                    font-weight: 700;
                }
                .daily-ranges-copy-all:disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                }
                .daily-ranges-copy-all:hover:not(:disabled) {
                    border-color: #fbbf24;
                    color: #fef3c7;
                    background: rgba(245, 158, 11, 0.26);
                }
                .daily-ranges-list {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    padding: 10px;
                }
                .daily-ranges-pagination {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .daily-ranges-pagination-bottom {
                    padding-top: 4px;
                }
                .daily-ranges-pagination-summary {
                    color: #94a3b8;
                    font-size: 12px;
                }
                .daily-ranges-pagination-controls {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    flex-wrap: wrap;
                }
                .daily-ranges-page-button {
                    min-width: 32px;
                    min-height: 28px;
                    border-radius: 8px;
                    border: 1px solid #334155;
                    background: rgba(15, 23, 42, 0.75);
                    color: #cbd5e1;
                    cursor: pointer;
                    padding: 4px 10px;
                    font-size: 12px;
                    font-weight: 700;
                }
                .daily-ranges-page-button:hover:not(:disabled) {
                    border-color: #38bdf8;
                    color: #e0f2fe;
                }
                .daily-ranges-page-button.active {
                    border-color: #38bdf8;
                    background: rgba(56, 189, 248, 0.16);
                    color: #7dd3fc;
                }
                .daily-ranges-page-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .daily-ranges-page-gap {
                    color: #64748b;
                    font-size: 12px;
                }
                .daily-range-card {
                    border: 1px solid #1e293b;
                    border-radius: 10px;
                    background: linear-gradient(180deg, #0b1220 0%, #0a101c 100%);
                    box-shadow: 0 6px 22px rgba(2, 6, 23, 0.32);
                }
                .daily-range-card-main {
                    padding: 12px 14px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    flex-wrap: wrap;
                    cursor: pointer;
                }
                .daily-range-card-main:hover {
                    background: rgba(125, 211, 252, 0.04);
                }
                .daily-range-title-wrap {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .daily-range-title {
                    color: #e2e8f0;
                    font-weight: 700;
                    letter-spacing: 0.2px;
                }
                .daily-range-title-button {
                    background: none;
                    border: none;
                    color: #e2e8f0;
                    font-weight: 700;
                    letter-spacing: 0.2px;
                    cursor: pointer;
                    padding: 0;
                }
                .daily-range-title-button:hover {
                    color: #7dd3fc;
                    text-decoration: underline;
                }
                .daily-range-copy-mini {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    min-height: 24px;
                    border-radius: 6px;
                    border: 1px solid rgba(148, 163, 184, 0.35);
                    background: rgba(15, 23, 42, 0.6);
                    color: #f8fafc;
                    cursor: pointer;
                    padding: 2px 8px;
                    font-size: 11px;
                    font-weight: 700;
                }
                .daily-range-copy-mini:hover {
                    border-color: rgba(56, 189, 248, 0.8);
                    color: #7dd3fc;
                }
                .daily-range-open-mini {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 24px;
                    border-radius: 6px;
                    border: 1px solid rgba(56, 189, 248, 0.55);
                    background: rgba(2, 132, 199, 0.18);
                    color: #bae6fd;
                    cursor: pointer;
                    padding: 2px 8px;
                    font-size: 11px;
                    font-weight: 700;
                }
                .daily-range-open-mini:hover {
                    background: rgba(2, 132, 199, 0.28);
                    color: #e0f2fe;
                }
                .daily-range-pill {
                    border-radius: 999px;
                    border: 1px solid transparent;
                    padding: 2px 10px;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                }
                .daily-range-pill.ok {
                    color: #22c55e;
                    border-color: rgba(34, 197, 94, 0.4);
                    background: rgba(34, 197, 94, 0.08);
                }
                .daily-range-pill.warn {
                    color: #f59e0b;
                    border-color: rgba(245, 158, 11, 0.4);
                    background: rgba(245, 158, 11, 0.08);
                }
                .daily-range-meta {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                    color: #94a3b8;
                    font-size: 12px;
                }
                .daily-range-meta span {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                }
            `}</style>

            {selectedRange && typeof document !== 'undefined' && createPortal((
                <div className="daily-range-modal-overlay" onClick={closeRangeDetails}>
                    <div
                        className="daily-range-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Range details for ${selectedRange}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="daily-range-modal-header">
                            <div>
                                <div className="daily-range-modal-title">Range Details</div>
                                <div className="daily-range-modal-subtitle">{selectedRange}</div>
                            </div>
                            <div className="daily-range-modal-actions">
                                <button
                                    type="button"
                                    className="daily-range-modal-copy"
                                    onClick={() => copyText(selectedRange, `modal-range-${selectedRange}`)}
                                >
                                    {copiedKey === `modal-range-${selectedRange}` ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                                    Copy Range
                                </button>
                                <button type="button" className="daily-range-modal-close" onClick={closeRangeDetails}><X size={16} /></button>
                            </div>
                        </div>

                        {detailsLoading && <div className="daily-range-modal-loading">Loading details...</div>}
                        {detailsError && <div className="daily-range-modal-error">{detailsError}</div>}

                        {!detailsLoading && !detailsError && rangeDetails && (
                            <div className="daily-range-modal-content">
                                <div className="daily-range-modal-section">
                                    <h4>SIDs ({rangeDetails.sid_rows?.length || 0})</h4>
                                    <div className="daily-range-sid-list">
                                        {(rangeDetails.sid_rows || []).map((item) => (
                                            <div key={`${item.sid}-${item.destination}`} className="daily-range-sid-row">
                                                <div className="sid">{item.sid}</div>
                                                <div className="dst">{item.destination}</div>
                                                <div className="occ">{item.occurrences}x</div>
                                            </div>
                                        ))}
                                        {!(rangeDetails.sid_rows || []).length && (
                                            <div className="daily-range-empty">No SID records found.</div>
                                        )}
                                    </div>
                                </div>

                                <div className="daily-range-modal-section">
                                    <h4>Ranges By Destination</h4>
                                    <div className="daily-range-destination-list">
                                        {(rangeDetails.destination_ranges || []).map((item) => {
                                            const copyKey = `destination-${item.destination}`;
                                            const joined = (item.ranges || []).join('\n');
                                            return (
                                                <div key={item.destination} className="daily-range-destination-card">
                                                    <div className="daily-range-destination-head">
                                                        <div className="destination-name">{item.destination}</div>
                                                        <button
                                                            type="button"
                                                            className="daily-range-modal-copy"
                                                            onClick={() => copyText(joined, copyKey)}
                                                        >
                                                            {copiedKey === copyKey ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                                                            Copy All Ranges
                                                        </button>
                                                    </div>
                                                    <div className="daily-range-destination-ranges">{(item.ranges || []).join(', ')}</div>
                                                </div>
                                            );
                                        })}
                                        {!(rangeDetails.destination_ranges || []).length && (
                                            <div className="daily-range-empty">No destination range groups found.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ), document.body)}

            <style>{`
                .daily-range-modal-overlay {
                    position: fixed;
                    inset: 0;
                    display: grid;
                    place-items: center;
                    padding: 20px;
                    background: rgba(2, 6, 23, 0.72);
                    backdrop-filter: blur(5px);
                    z-index: 9999;
                    overflow-y: auto;
                }
                .daily-range-modal {
                    position: relative;
                    width: min(980px, 100%);
                    max-height: min(820px, calc(100dvh - 40px));
                    overflow: auto;
                    background: linear-gradient(180deg, #0d1527 0%, #091121 100%);
                    border: 1px solid #24364e;
                    border-radius: 16px;
                    box-shadow: 0 28px 64px rgba(2, 6, 23, 0.58);
                    animation: dailyRangeModalIn 180ms ease-out;
                }
                .daily-range-modal-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 16px 18px;
                    border-bottom: 1px solid #223248;
                    background: rgba(12, 24, 42, 0.85);
                    backdrop-filter: blur(3px);
                    position: sticky;
                    top: 0;
                    z-index: 2;
                }
                .daily-range-modal-title {
                    color: #f8fafc;
                    font-weight: 700;
                    font-size: 16px;
                }
                .daily-range-modal-subtitle {
                    color: #9bdafc;
                    margin-top: 4px;
                    font-size: 12px;
                    letter-spacing: 0.02em;
                    word-break: break-word;
                }
                .daily-range-modal-actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .daily-range-modal-copy {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    min-height: 38px;
                    background: #18263c;
                    border: 1px solid #304563;
                    color: #dbeafe;
                    border-radius: 10px;
                    padding: 8px 12px;
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: 700;
                    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
                }
                .daily-range-modal-copy:hover {
                    background: #20314a;
                    border-color: #4b678f;
                }
                .daily-range-modal-copy:active {
                    transform: scale(0.98);
                }
                .daily-range-modal-close {
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    border: 1px solid #304563;
                    background: #111f33;
                    color: #dbeafe;
                    cursor: pointer;
                    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
                }
                .daily-range-modal-close:hover {
                    background: #1a2d48;
                    border-color: #4b678f;
                }
                .daily-range-modal-close:active {
                    transform: scale(0.97);
                }
                .daily-range-modal-content {
                    padding: 16px 18px 18px;
                    display: grid;
                    gap: 14px;
                }
                .daily-range-modal-section h4 {
                    margin: 0 0 8px;
                    color: #d5e3f6;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                }
                .daily-range-sid-list, .daily-range-destination-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .daily-range-sid-row {
                    display: grid;
                    grid-template-columns: minmax(130px, 1fr) minmax(150px, 2fr) auto;
                    gap: 8px;
                    padding: 10px;
                    border: 1px solid #223248;
                    border-radius: 10px;
                    background: #101c31;
                    color: #e2e8f0;
                }
                .daily-range-sid-row .dst {
                    color: #cbd5e1;
                }
                .daily-range-sid-row .occ {
                    color: #ffd166;
                    font-weight: 700;
                }
                .daily-range-destination-card {
                    border: 1px solid #223248;
                    border-radius: 10px;
                    background: #101c31;
                    padding: 12px;
                }
                .daily-range-destination-head {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    margin-bottom: 8px;
                }
                .destination-name {
                    color: #e2e8f0;
                    font-weight: 700;
                }
                .daily-range-destination-ranges {
                    color: #cbd5e1;
                    line-height: 1.5;
                    font-size: 13px;
                }
                .daily-range-modal-loading,
                .daily-range-modal-error,
                .daily-range-empty {
                    color: #cbd5e1;
                    padding: 16px 18px;
                }
                .daily-range-modal-error {
                    color: #fca5a5;
                }
                @keyframes dailyRangeModalIn {
                    from {
                        opacity: 0;
                        transform: translateY(12px) scale(0.985);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                @media (max-width: 768px) {
                    .daily-ranges-list-header {
                        align-items: flex-start;
                    }
                    .daily-ranges-list-actions,
                    .daily-ranges-pagination {
                        width: 100%;
                        justify-content: space-between;
                    }
                    .daily-ranges-pagination-controls {
                        width: 100%;
                    }
                    .daily-range-modal-overlay {
                        padding: 12px;
                    }
                    .daily-range-modal {
                        width: 100%;
                        max-height: calc(100dvh - 24px);
                        border-radius: 14px;
                    }
                    .daily-range-modal-header {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .daily-range-modal-actions {
                        justify-content: space-between;
                    }
                    .daily-range-modal-copy {
                        flex: 1;
                        justify-content: center;
                    }
                    .daily-range-sid-row {
                        grid-template-columns: 1fr;
                        gap: 4px;
                    }
                    .daily-range-destination-head {
                        flex-direction: column;
                        align-items: stretch;
                    }
                }
                @media (prefers-reduced-motion: reduce) {
                    .daily-range-modal,
                    .daily-range-modal-copy,
                    .daily-range-modal-close {
                        animation: none;
                        transition: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default DailyRanges;
